import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import {
  Gauge, CircleCheck, Circle, AlertTriangle, ShieldAlert, ShieldCheck, Layers2, DoorOpen,
} from "lucide-react";

export const CLASS_TONE = {
  finance_ready: "text-[#00E65B] border-[#00E65B]/40",
  funding_preparation: "text-accent border-accent/50",
  development_required: "text-[#FFB800] border-[#FFB800]/40",
  concept_stage: "text-muted-foreground border-border",
};
const GATE_LABELS = {
  not_triggered: "Not yet triggered", passed: "Passed — funding assessed",
  overridden: "Management override recorded", not_required: "Confirmed — no funding required",
};

function FlagRow({ f, iid, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const submit = async () => {
    try {
      await api.post(`/susfin/fundability/${iid}/review-flag`, { flag: f.key, note });
      toast.success("Flag marked as reviewed");
      setOpen(false); onReviewed();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="border border-border rounded-sm p-2.5" data-testid={`sf-gw-flag-${f.key}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${f.severity === "material" ? "text-red-400" : "text-[#FFB800]"}`} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium flex items-center gap-2 flex-wrap">
            {f.label}
            <Badge variant="outline" className="text-[8px] uppercase">{f.severity}</Badge>
            {f.reviewed && <Badge variant="outline" className="text-[8px] uppercase text-[#00E65B] border-[#00E65B]/40">Reviewed</Badge>}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{f.detail}</div>
          {f.reviewed && f.review && <div className="text-[10px] text-muted-foreground mt-1">Reviewed by {f.review.by}{f.review.note ? ` — ${f.review.note}` : ""}</div>}
          {open && (
            <div className="flex gap-2 mt-2">
              <Input className="h-7 text-xs" placeholder="Review note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`sf-gw-note-${f.key}`} />
              <Button size="sm" variant="primary" className="h-7" onClick={submit} data-testid={`sf-gw-confirm-${f.key}`}>Confirm</Button>
            </div>
          )}
        </div>
        {!f.reviewed && !open && (
          <Button size="sm" variant="outline" className="h-6 text-[10px] shrink-0" onClick={() => setOpen(true)} data-testid={`sf-gw-review-${f.key}`}>
            Mark reviewed
          </Button>
        )}
      </div>
    </div>
  );
}

export default function FundabilityPanel({ id }) {
  const [d, setD] = useState(null);
  const load = () => api.get(`/susfin/fundability/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [id]);
  if (!d) return <div className="p-4 text-muted-foreground font-mono text-sm">Scoring…</div>;

  const cls = d.classification;
  return (
    <div className="space-y-4" data-testid="sf-fundability-panel">
      <div className="border border-border rounded-sm p-4 flex items-center gap-4">
        <Gauge className="h-6 w-6 text-accent shrink-0" />
        <div>
          <div className="font-display text-3xl font-bold leading-none" data-testid="sf-fund-score">
            {d.score}<span className="text-sm text-muted-foreground">/100</span>
          </div>
          <Badge variant="outline" className={`text-[9px] uppercase mt-1.5 ${CLASS_TONE[cls.value]}`} data-testid="sf-fund-class">{cls.label}</Badge>
        </div>
        {cls.capped && (
          <p className="text-[11px] text-[#FFB800] flex items-start gap-1 flex-1" data-testid="sf-fund-capped">
            <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {cls.cap_note}
          </p>
        )}
      </div>

      <div>
        <div className="label-eyebrow text-[10px] mb-2">Score components</div>
        <div className="space-y-2">
          {d.components.map((c) => (
            <div key={c.key} data-testid={`sf-fund-comp-${c.key}`}>
              <div className="flex justify-between text-[11px] mb-0.5">
                <span>{c.label}</span>
                <span className="font-mono text-muted-foreground">{c.achieved}/{c.max}</span>
              </div>
              <div className="h-1.5 bg-border rounded-full overflow-hidden">
                <div className="h-full bg-accent/70" style={{ width: `${(c.achieved / c.max) * 100}%` }} />
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                {c.items.map((x) => (
                  <span key={x.label} className={`text-[10px] inline-flex items-center gap-1 ${x.done ? "text-muted-foreground" : "text-[#FFB800]"}`}>
                    {x.done ? <CircleCheck className="h-2.5 w-2.5 text-[#00E65B]" /> : <Circle className="h-2.5 w-2.5" />}{x.label}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {d.recommendations.length > 0 && (
        <div className="border border-accent/30 rounded-sm p-3" data-testid="sf-fund-recs">
          <div className="label-eyebrow text-[10px] mb-1.5">Actions to raise the score</div>
          <ul className="text-[11px] space-y-0.5">
            {d.recommendations.map((r, idx) => <li key={idx}>· {r}</li>)}
          </ul>
        </div>
      )}

      {d.coaching && (
        <div className="border border-accent/40 rounded-sm p-3 bg-accent/5" data-testid="sf-fund-coaching">
          <div className="label-eyebrow text-[10px] mb-1.5 inline-flex items-center gap-1">
            <Gauge className="h-3 w-3 text-accent" /> Coaching checklist
          </div>
          {d.coaching.at_top ? (
            <p className="text-[11px] text-[#00E65B]" data-testid="sf-coaching-top">
              Top tier reached — this investment is Finance Ready. Keep evidence current to hold the rating.
            </p>
          ) : (
            <p className="text-[11px] mb-2" data-testid="sf-coaching-target">
              <span className="font-semibold">{d.coaching.points_to_next} point{d.coaching.points_to_next === 1 ? "" : "s"}</span> to reach <span className="font-semibold">{d.coaching.next_band}</span>. Highest-impact steps first:
            </p>
          )}
          {d.coaching.checklist.length > 0 && (
            <ul className="space-y-1">
              {d.coaching.checklist.map((c, idx) => (
                <li key={idx} className="text-[11px] flex items-start gap-2" data-testid={`sf-coaching-item-${idx}`}>
                  <Circle className="h-2.5 w-2.5 text-[#FFB800] mt-1 shrink-0" />
                  <span className="flex-1">{c.action}<span className="text-muted-foreground"> · {c.area}</span></span>
                  <Badge variant="outline" className="text-[8px] shrink-0">+{c.points}</Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div>
        <div className="label-eyebrow text-[10px] mb-2 inline-flex items-center gap-1">
          <ShieldAlert className="h-3 w-3 text-[#FFB800]" /> Greenwashing risk check
          {d.greenwashing.material_open > 0 && <Badge variant="outline" className="text-[8px] text-red-400 border-red-400/40 uppercase" data-testid="sf-gw-open-count">{d.greenwashing.material_open} material open</Badge>}
        </div>
        {d.greenwashing.flags.length === 0 ? (
          <p className="text-[11px] text-[#00E65B] inline-flex items-center gap-1" data-testid="sf-gw-clear"><ShieldCheck className="h-3.5 w-3.5" /> No greenwashing risk flags detected.</p>
        ) : (
          <div className="space-y-1.5">
            {d.greenwashing.flags.map((f) => <FlagRow key={f.key} f={f} iid={id} onReviewed={load} />)}
          </div>
        )}
      </div>

      <div>
        <div className="label-eyebrow text-[10px] mb-2 inline-flex items-center gap-1"><Layers2 className="h-3 w-3 text-accent" /> Double-funding control</div>
        {d.double_funding.flags.length === 0 ? (
          <p className="text-[11px] text-muted-foreground" data-testid="sf-df-clear">No overlapping-funding flags detected.</p>
        ) : (
          <div className="space-y-1.5">
            {d.double_funding.flags.map((f) => (
              <div key={f.key} className="border border-border rounded-sm p-2.5" data-testid={`sf-df-flag-${f.key}`}>
                <div className="text-xs font-medium flex items-center gap-2">
                  <AlertTriangle className={`h-3.5 w-3.5 ${f.severity === "material" ? "text-red-400" : "text-[#FFB800]"}`} />
                  {f.label}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{f.detail}</div>
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground">Flagged for review — combining schemes is not automatically prohibited; check each scheme's stacking rules.</p>
          </div>
        )}
      </div>

      <div className="border border-border rounded-sm p-3 flex items-center gap-2" data-testid="sf-gate-status">
        <DoorOpen className="h-4 w-4 text-accent shrink-0" />
        <div className="text-[11px]">
          <span className="text-muted-foreground uppercase tracking-wider text-[9px]">Funding gate · </span>
          {GATE_LABELS[d.gate.status] || d.gate.status}
          {d.gate.note ? <span className="text-muted-foreground"> — {d.gate.note}</span> : null}
          {d.gate.by ? <span className="text-muted-foreground"> ({d.gate.by})</span> : null}
        </div>
      </div>

      <p className="text-[10px] text-muted-foreground">{d.disclaimer}</p>
    </div>
  );
}
